import { PlusOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Button, Flex, Form, Popconfirm, Space, Table, Tooltip, Typography } from 'antd';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { TFunction } from 'i18next';
import { Dispatch, SetStateAction, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getLeaveStatusesDropdown } from 'src/apis/hr_ticket/leaveStatus.api';
import { getHolidaysDropdown } from 'src/apis/master_data/holiday.api';
import { UTCNoShiftDatePicker } from 'src/components/v2/DatePicker';
import { EmployeeSelectV2, LeaveStatusSelect } from 'src/components/v2/Select';
import { LeaveStatusEn } from 'src/constants/leaveStatus.enum';
import { AppContext } from 'src/contexts/app.context';
import { ILeaveStatus } from 'src/types/hr_ticket/leaveStatus.type';
import { ILeaveTicketDetail, LeaveTicketDetailAdd } from 'src/types/hr_ticket/leaveTicket.type';
import { IEmployeeShort, UserInfo } from 'src/types/master_data/employee.type';
import { showNotification } from 'src/utils/notification';
import { formatDate } from 'src/utils/utils';
import { v4 as uuid } from 'uuid';
import './ConfirmOvertimeTicket.scss';
const { Text } = Typography;
dayjs.extend(utc);

interface Props {
  lastDateOvertime: Date;
  compensatoryLeaveDetails: LeaveTicketDetailAdd[];
  handleChangeFormState: (leaveTicketDetails: ILeaveTicketDetail[]) => void;
  overtimeEmployees: IEmployeeShort[];
}

type FormStateType = LeaveTicketDetailAdd;

const initialFormState: FormStateType = {
  id: undefined,
  employeeId: undefined,
  employee: undefined,
  leaveTypeId: undefined,
  leaveType: undefined,
  leaveStatusId: undefined,
  leaveStatus: undefined,
  date: undefined
};

const columns = function (
  userInfo: UserInfo | null,
  formState: FormStateType,
  setFormState: Dispatch<SetStateAction<LeaveTicketDetailAdd>>,
  callbackSave: () => void,
  callbackEdit: (record: Partial<ILeaveTicketDetail>) => void,
  callbackCancel: (record: Partial<ILeaveTicketDetail>) => void,
  callbackDelete: (record: Partial<ILeaveTicketDetail>) => void,
  handleLeaveStatusChange: (value: string) => void,
  editingKey: string,
  disableDate: (current: dayjs.Dayjs) => boolean,
  overtimeEmployees: IEmployeeShort[],
  t: TFunction<'translation', undefined, 'translation'>
): ColumnsType<Partial<ILeaveTicketDetail>> {
  return [
    {
      title: (
        <Space size={[4, 0]}>
          <Tooltip title='Bắt buộc'>
            <Text type='danger'>*</Text>
          </Tooltip>
          <Text>{t('Người nghỉ')}</Text>
        </Space>
      ),
      dataIndex: 'employee',
      width: '18%',
      key: 'employee',
      render: (_, record) => {
        if (formState.id === record.id) {
          return (
            <Form.Item name='employee' rules={[{ required: true, message: '' }]} style={{ marginBottom: 0 }}>
              <EmployeeSelectV2
                dataSource={overtimeEmployees}
                onChange={(value: any, option: any) => {
                  const data = option?.data;
                  setFormState((prev) => ({ ...prev, employee: data, employeeId: value }));
                }}
                departmentId={userInfo?.departmentId}
                allowClear
              />
            </Form.Item>
          );
        }
        return <Text>{record.employee?.fullName}</Text>;
      }
    },
    {
      title: (
        <Space size={[4, 0]}>
          <Tooltip title='Bắt buộc'>
            <Text type='danger'>*</Text>
          </Tooltip>
          <Text>{t('Ngày nghỉ')}</Text>
        </Space>
      ),
      dataIndex: 'date',
      width: '18%',
      render: (_, record) => {
        if (formState.id === record.id) {
          return (
            <Form.Item name='date' rules={[{ required: true, message: '' }]} style={{ marginBottom: 0 }}>
              <UTCNoShiftDatePicker
                allowClear
                disabledDate={disableDate}
                onChangeUTC={(value) => {
                  setFormState((prev) => ({ ...prev, date: value || undefined }));
                }}
              />
            </Form.Item>
          );
        }
        return <Text>{formatDate(record.date)}</Text>;
      }
    },
    {
      title: (
        <Space size={[4, 0]}>
          <Tooltip title='Bắt buộc'>
            <Text type='danger'>*</Text>
          </Tooltip>
          <Text>{t('Thời gian nghỉ')}</Text>
        </Space>
      ),
      dataIndex: 'leaveStatus',
      width: '18%',
      render: (_, record) => {
        if (formState.id === record.id) {
          return (
            <Form.Item name='leaveStatus' rules={[{ required: true, message: '' }]} style={{ marginBottom: 0 }}>
              <LeaveStatusSelect onChange={handleLeaveStatusChange} />
            </Form.Item>
          );
        }
        return <Text>{record.leaveStatus?.name}</Text>;
      }
    },
    {
      title: 'Thao tác',
      key: 'action',
      width: '10%',
      render: (_, record) => {
        if (formState.id === record.id) {
          return (
            <Space size='small'>
              <Button type='link' size='small' onClick={callbackSave}>
                {t('action.save')}
              </Button>
              <Button type='link' size='small' danger onClick={() => callbackCancel(record)}>
                {t('action.cancel')}
              </Button>
            </Space>
          );
        }
        return (
          <Space size='small'>
            <Button type='link' disabled={editingKey !== ''} size='small' onClick={() => callbackEdit(record)}>
              {t('action.edit')}
            </Button>
            <Popconfirm title={t('notification.warning.delete')} onConfirm={() => callbackDelete(record)}>
              <Button type='link' size='small' danger>
                {t('action.delete')}
              </Button>
            </Popconfirm>
          </Space>
        );
      }
    }
  ];
};

const AddLeaveTicketDetail = ({
  handleChangeFormState,
  lastDateOvertime,
  compensatoryLeaveDetails,
  overtimeEmployees
}: Props) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [leaveStatus, setLeaveStatus] = useState<ILeaveStatus>();
  const [addMode, setAddMode] = useState<'add' | 'edit'>('add');
  const [formState, setFormState] = useState(initialFormState);
  const [leaveTicketDetails, setLeaveTicketDetails] = useState<ILeaveTicketDetail[]>([]);
  const [editingKey, setEditingKey] = useState('');
  const { userInfo } = useContext(AppContext);

  useEffect(() => {
    setLeaveTicketDetails(compensatoryLeaveDetails as ILeaveTicketDetail[]);
  }, [compensatoryLeaveDetails]);

  const leaveStatusQuery = useQuery({
    queryKey: ['leaveStatus'],
    queryFn: () => getLeaveStatusesDropdown(),
    staleTime: 60 * 1000
  });

  const holidayQuery = useQuery({
    queryKey: ['holiday'],
    queryFn: () => getHolidaysDropdown(),
    staleTime: 60 * 1000
  });

  const disableDate = (current: dayjs.Dayjs) => {
    const holidays = holidayQuery.data?.data.map(({ date, ..._ }) => formatDate(date));

    const isSunday = current.day() === 0;
    const isHoliday = holidays?.includes(current.format('DD/MM/YYYY'));

    return isSunday || isHoliday || current.isBefore(lastDateOvertime);
  };

  const handleAddNewRow = (leaveTicketDetails: ILeaveTicketDetail[]) => {
    if (editingKey) {
      showNotification('Vui lòng hoàn thành dòng dữ liệu đang thao tác.', 'info');
    } else {
      const newRow: FormStateType = {
        ...initialFormState,
        id: uuid(),
        leaveStatusId: leaveStatus?.id,
        leaveStatus: leaveStatus
      };
      setLeaveTicketDetails((prev) => [...prev, newRow as ILeaveTicketDetail]);
      setEditingKey(String(newRow.id));
      setFormState(newRow);
    }
    setAddMode('add');
  };

  const handleEdit = (record: Partial<ILeaveTicketDetail>) => {
    setFormState(record);
    setAddMode('edit');
    setEditingKey(String(record.id));
  };

  const handleCancel = (record: Partial<ILeaveTicketDetail>) => {
    if (addMode === 'add') {
      const newLeaveTicketDetails = leaveTicketDetails.filter((x) => x.id !== record.id);
      setLeaveTicketDetails(newLeaveTicketDetails);
      setEditingKey('');
    } else {
      setFormState(initialFormState);
      setEditingKey('');
    }
  };

  const handleDelete = (record: Partial<ILeaveTicketDetail>) => {
    const newLeaveTicketDetails = leaveTicketDetails.filter((x) => x.id !== record.id);
    setLeaveTicketDetails(newLeaveTicketDetails);
    handleChangeFormState(newLeaveTicketDetails);
  };

  const handleSave = () => {
    form
      .validateFields()
      .then(() => {
        let cloneDataSource = [...leaveTicketDetails];
        const sameLeaveTicket = cloneDataSource.find((item) => {
          return (
            item.id !== formState.id &&
            formatDate(item.date) === formatDate(formState.date) &&
            item.employeeId === formState.employeeId
          );
        });

        if (formState.leaveStatusId === sameLeaveTicket?.leaveStatusId) {
          showNotification('Đã tồn tại dòng dữ liệu với thời gian tương tự');
        } else if (formState.leaveStatusId === LeaveStatusEn.FULL_DAY && sameLeaveTicket) {
          showNotification('Thời gian nghỉ không thể vượt quá 1 ngày');
        } else {
          const index = cloneDataSource?.findIndex((item) => item.id === formState.id);
          if (index === -1) return;

          cloneDataSource[index] = formState as ILeaveTicketDetail;
          setLeaveTicketDetails(cloneDataSource);
          setFormState(initialFormState);
          setEditingKey('');
          handleChangeFormState(cloneDataSource);
        }
      })
      .catch(() => {});
  };

  const handleLeaveStatusChange = (value: string) => {
    const leaveStatus = value ? JSON.parse(value) : undefined;
    setFormState((prev) => ({ ...prev, leaveStatus: leaveStatus, leaveStatusId: leaveStatus?.id }));
  };

  useEffect(() => {
    const leaveStatus = leaveStatusQuery.data?.data.find((x) => x.id === LeaveStatusEn.FULL_DAY);
    if (leaveStatus) {
      setLeaveStatus(leaveStatus);
    }
  }, [leaveStatusQuery.data?.data]);

  useEffect(() => {
    if (leaveStatus) {
      const newRow: FormStateType = {
        ...initialFormState,
        id: uuid(),
        leaveStatusId: leaveStatus?.id,
        leaveStatus: leaveStatus
      };
      setLeaveTicketDetails((prev) => [...prev, newRow as ILeaveTicketDetail]);
      setEditingKey(String(newRow.id));
      setFormState(newRow);
      setAddMode('add');
    }
  }, [leaveStatus, setEditingKey]);

  return (
    <Form
      form={form}
      fields={[
        {
          name: 'employee',
          value: formState.employeeId
        },
        {
          name: 'leaveType',
          value: JSON.stringify(formState.leaveType)
        },
        {
          name: 'leaveStatus',
          value: formState.leaveStatusId
        },
        {
          name: 'date',
          value: formState.date ? dayjs(formState.date) : undefined
        },
        {
          name: 'attaches',
          value: formState.leaveTicketDetailAttaches
            ? formState.leaveTicketDetailAttaches.map(({ overtimeTicket }) => JSON.stringify(overtimeTicket))
            : undefined
        }
      ]}
    >
      <Table
        rowKey={(record) => String(record.id)}
        columns={columns(
          userInfo,
          formState,
          setFormState,
          handleSave,
          handleEdit,
          handleCancel,
          handleDelete,
          handleLeaveStatusChange,
          editingKey,
          disableDate,
          overtimeEmployees,
          t
        )}
        dataSource={leaveTicketDetails}
        className='leave-ticket-details-table'
        pagination={false}
        footer={() => (
          <Flex justify='center'>
            <Button
              onClick={() => handleAddNewRow(leaveTicketDetails)}
              icon={<PlusOutlined />}
              className='add-new-row-btn'
              type='link'
            >
              Thêm nhân viên nghỉ bù
            </Button>
          </Flex>
        )}
      />
    </Form>
  );
};

export default AddLeaveTicketDetail;
